import {
  SettingOutlined,
  ControlOutlined,
  LockOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  PhoneOutlined,
  QuestionCircleOutlined,
  DashboardOutlined,
  SoundOutlined,
  ContactsOutlined,
  UserOutlined,
  TeamOutlined,
  SolutionOutlined,
  FileDoneOutlined,
  ProfileOutlined,
} from '@ant-design/icons';

import DefaultRedirectPage from '../pages/DefaultRedirect/DefaultRedirectPage';
import { EnumsValues, PageKey } from '../enums/EnumsValues';
import { IRouteConfig } from '../interfaces/routeConfig';
import { Suspense, lazy } from 'react';
import { PageLoading } from '@ant-design/pro-layout';
import AdminInvitationPage from '../pages/AdminInvitation/AdminInvitationPage';
import InvitationPage from '../pages/Invitation/Invitation';
import { Redirect } from 'react-router';
import EmptyLayout from '../layouts/emptyLayout/index';
import { LocationHomeIcon } from '../components/common/icons/LocationHomeIcon';

const ReportDetailPage = lazy(
  () => import('../pages/Reports/ReportDetailPage'),
);
const Indicators = lazy(() => import('../pages/Dashboards/Indicators'));
const ChairLoadPercentageBoard = lazy(
  () => import('../pages/Dashboards/ChairLoadPercentageBoard'),
);
const ReunionBoard = lazy(() => import('../pages/Dashboards/ReunionsBoard'));
const Board = lazy(() => import('../pages/HelpDesk/Board'));
const CompleteDetail = lazy(() => import('../pages/HelpDesk/CompleteDetail'));
const ZohoDesk = lazy(() => import('../pages/HelpDesk/ZohoDesk'));
const GuideChairs = lazy(() => import('../pages/Guides/GuideChairs'));
const GuideStaff = lazy(() => import('../pages/Guides/GuideStaff'));
const MembersBoard = lazy(() => import('../pages/ChairBoard/MembersBoard'));
const GroupsPage = lazy(() => import('../pages/GroupsVistage/GroupsPage'));
const VcgPage = lazy(() => import('../pages/GroupsVistage/VcgPage'));
const TaxConditionPage = lazy(
  () => import('../pages/EntryForm/TaxConditionPage'),
);
const EntryFormsPage = lazy(() => import('../pages/EntryForm/EntryFormsPage'));
const PendingEntryFormsPage = lazy(
  () => import('../pages/EntryForm/PendingEntryFormsPage'),
);
const CompanyPage = lazy(() => import('../pages/Company/CompanyPage'));
const ExhibitorFormPage = lazy(
  () => import('../pages/ExhibitorForm/ExhibitorFormPage'),
);
const ExternalPresenterInvoiceFormPage = lazy(
  () => import('../pages/PresenterInvoice/ExternalPresenterInvoiceFormPage'),
);
const EntryFormPublicPage = lazy(
  () => import('../pages/EntryForm/EntryFormPublicPage'),
);
const LoginPage = lazy(() => import('../pages/Login/LoginPage'));
const PublicLayout = lazy(() => import('../layouts/publicLayout'));
const RegisteredLayout = lazy(() => import('../layouts/registered'));
const AppSettingPage = lazy(() => import('../pages/AppSetting/AppSettingPage'));
const ResetPasswordContentPage = lazy(
  () => import('../pages/ResetPassword/ResetPasswordContentPage'),
);
const SetPasswordContentPage = lazy(
  () => import('../pages/SetPassword/SetPasswordPage'),
);
const ProfilePage = lazy(() => import('../pages/Profile/ProfilePage'));
const RolePage = lazy(() => import('../pages/Role/RolePage'));
const PermissionPage = lazy(() => import('../pages/Permission/PermissionPage'));
const UserPage = lazy(() => import('../pages/User/UserPage'));
const AboutPage = lazy(() => import('../pages/About/AboutPage'));
const Reminder2FA = lazy(
  () => import('../pages/Totp2FA/Reminder2FA/Reminder2FA'),
);
const Generate2FA = lazy(
  () => import('../pages/Totp2FA/Generate2FA/Generate2FA'),
);
const Validate2FA = lazy(
  () => import('../pages/Totp2FA/Validate2FA/Validate2FA'),
);
const Config2FA = lazy(() => import('../pages/Totp2FA/Config2FA/Config2FA'));
const TenantPage = lazy(() => import('../pages/Tenant/TenantPage'));
const CompleteRegisterPage = lazy(
  () => import('../pages/CompleteRegister/CompleteRegisterPage'),
);
const MustChangePasswordPage = lazy(
  () => import('../pages/MustChangePassword/MustChangePasswordPage'),
);
const AuditPage = lazy(() => import('../pages/Audit/AuditPage'));
const RegisterPage = lazy(() => import('../pages/Register/RegisterPage'));
const MaintenancePage = lazy(
  () => import('../pages/Maintenance/MaintenancePage'),
);
const StatusPage = lazy(() => import('../pages/Status/StatusPage'));
const TermsAndConditionsPage = lazy(
  () => import('../pages/TermsAndConditions/TermsAndConditionsPage'),
);
const ManageTermsAndConditionsPage = lazy(
  () =>
    import('../pages/ManageTermsAndConditions/ManageTermsAndConditionsPage'),
);
const SelectTenantPage = lazy(
  () => import('../pages/SelectTenantPage/SelectTenant'),
);
const ProgramsPage = lazy(() => import('../pages/Programs/ProgramsPage'));
const ProgramCategoriesPage = lazy(
  () => import('../pages/Programs/ProgramCategoriesPage'),
);
const ProgramVariantsPage = lazy(
  () => import('../pages/Programs/ProgramVariants/ProgramVariantsPage'),
);
const ProgramRegionsPage = lazy(
  () => import('../pages/Programs/ProgramRegionsPage'),
);
const UsaProgramsPage = lazy(() => import('../pages/Programs/UsaProgramsPage'));
const UpdatePricesPage = lazy(
  () => import('../pages/Programs/UpdatePricesPage'),
);
// Oculto temporalmente junto con el menú "Eventos" en Comunicación.
// const EventsPage = lazy(() => import('../pages/Events/EventsPage'));
const LandingEventPage = lazy(() => import('../pages/Events/LandingEventPage'));
const Categories = lazy(() => import('../pages/Exhibitors/Categories'));
const Subcategories = lazy(() => import('../pages/Exhibitors/Subcategories'));
const HitoTiposPage = lazy(() => import('../pages/Hitos/HitoTiposPage'));
const HitoSubtiposPage = lazy(() => import('../pages/Hitos/HitoSubtiposPage'));

const ContactsPage = lazy(() => import('../pages/community/ContactsPage'));
const StaffPage = lazy(() => import('../pages/community/StaffPage'));
const ChairPage = lazy(() => import('../pages/community/ChairPage'));
const MembersPage = lazy(() => import('../pages/community/Members/MembersPage'));
const ProspectsPage = lazy(() => import('../pages/community/ProspectsPage'));
const ExhibitorPage = lazy(() => import('../pages/Reunions/ExhibitorPage'));
const MeetingsPage = lazy(() => import('../pages/Reunions/Meetings/MeetingsPage'));
const TopicsPage = lazy(() => import('../pages/Reunions/Topics/TopicsPage'));
const PresentationsPage = lazy(() => import('../pages/Reunions/Presentations/PresentationsPage'));
const EvaluationForm = lazy(() => import('../pages/Reunions/Meetings/EvaluationForm'));
const GroupEvaluationPage = lazy(() => import('../pages/Reunions/Meetings/GroupEvaluationPage'));
const ContactLogPage = lazy(() => import('../pages/ContactLog/ContactLogPage'));
const GroupLogPage = lazy(() => import('../pages/GroupLog/GroupLogPage'));
const MyMembersChairPage = lazy(() => import('../pages/MembersChair/MyMembersChairPage'));
const ChairRequestsPage = lazy(() => import('../pages/MembersChair/ChairRequestsPage'));
const RequestsPage = lazy(() => import('../pages/Requests/RequestsPage'));
const LeavePollsPage = lazy(() => import('../pages/LeavePoll/LeavePollsPage'));

const MyAccountPage = lazy(() => import('../pages/MyAccount/MyAccountPage'));
const MyExhibitorProfilePage = lazy(() => import('../pages/MyExhibitorProfile/MyExhibitorProfilePage'));
const SuppliersPage = lazy(() => import('../pages/Suppliers/SuppliersPage'));
const SupplierManagementPage = lazy(
  () => import('../pages/SupplierManagement/SupplierManagementPage'),
);
const ResendConfirmationAccountEmailPage = lazy(
  () =>
    import(
      '../pages/ResendConfirmationAccountEmail/ResendConfirmationAccountEmailPage'
    ),
);
const OrderPaymentPage = lazy(
  () => import('../pages/OrderPayment/OrderPaymentPage'),
);

const NotificationsPage = lazy(
  () => import('../pages/Notifications/NotificationsPage'),
);

const SurveyCampaignsPage = lazy(
  () => import('../pages/surveys/SurveyCampaignsPage'),
);

const redirectIfNotExist = {
  path: '*',
  component: () => <Redirect to="/" />,
  key: PageKey.RedirectIfNotExist,
  hideInMenu: true,
};

const routes: IRouteConfig[] = [
  {
    path: '/',
    exact: true,
    component: DefaultRedirectPage,
    key: PageKey.DefaultRedirect,
  },
  {
    path: '/app',
    component: (props: any) => (
      <Suspense fallback={<PageLoading />}>
        <RegisteredLayout {...props} />
      </Suspense>
    ),
    key: PageKey.App,
    routes: [
      {
        path: '/app/boards',
        name: 'Tablero',
        translation_key: 'pages.generalDashboard',
        component: EmptyLayout,
        icon: <DashboardOutlined />,
        authority: EnumsValues.Functions.AdministrationMenuRead,
        key: PageKey.GeneralDashboard,
        hideInMobileMenu: false,
        hideInMenu: (routeConfig) => {
          return !!routeConfig.isExhibitorAccount;
        },
        routes: [
          {
            path: '/app/boards/indicators',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <Indicators />
              </Suspense>
            ),
            authority: EnumsValues.Functions.ReportEmbedIndicatorsRead,
            name: 'Indicadores',
            translation_key: 'pages.indicators',
            key: PageKey.Indicators,
            requiresTenantSelected: true, //cambiar
            hideInMenu: (routeConfig) => {
              return !routeConfig.isStaffAccount;
            },
          },
          {
            path: '/app/boards/reunions',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <ReunionBoard />
              </Suspense>
            ),
            name: 'Reuniones',
            authority: EnumsValues.Functions.ReportEmbedReunionsRead,
            translation_key: 'pages.reunions',
            key: PageKey.Reunions,
            requiresTenantSelected: false, //cambiar
            hideInMenu: (routeConfig) => {
              return !routeConfig.isStaffAccount;
            },
          },
          {
            path: '/app/boards/members',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <MembersBoard />
              </Suspense>
            ),
            authority: EnumsValues.Functions.ReportEmbedMembersRead,
            name: 'Miembros',
            translation_key: 'pages.boardMembers',
            key: PageKey.BoardMembers,
            requiresTenantSelected: true,
            hideInMenu: (routeConfig) => {
              return !routeConfig.isChairAccount;
            },
          },
          {
            path: '/app/boards/chair-reunions',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <ReunionBoard />
              </Suspense>
            ),
            name: 'Reuniones',
            authority: EnumsValues.Functions.ReportEmbedReunionsRead,
            translation_key: 'pages.reunions',
            key: PageKey.ChairReunions,
            requiresTenantSelected: true,
            hideInMenu: (routeConfig) => {
              return !routeConfig.isChairAccount;
            },
          },
          {
            path: '/app/boards/chair-load-percentage',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <ChairLoadPercentageBoard />
              </Suspense>
            ),
            name: '% Carga Chairs',
            translation_key: 'pages.chair_load_percentaje',
            authority: EnumsValues.Functions.ReportEmbedChairLoadRead,
            key: PageKey.ChairLoadPercentage,
            requiresTenantSelected: false, //cambiar
            hideInMenu: () => {
              return true;
            },
          },
        ],
      },
      {
        path: '/app/people',
        name: 'Personas',
        translation_key: 'pages.people',
        component: EmptyLayout,
        icon: <UserOutlined />,
        hideInMenu: (routeConfig) => {
          return !routeConfig.isStaffAccount;
        },
        authority: EnumsValues.Functions.AdministrationMenuRead,
        key: PageKey.People,
        hideInMobileMenu: false,
        routes: [
          {
            path: '/app/people/contacts',
            name: 'Contactos',
            translation_key: 'pages.communityContacts',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <ContactsPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.ContactList,
            key: PageKey.CommunityContacts,
          },
          {
            path: '/app/people/contactLog',
            name: 'Historial de Contactos',
            translation_key: 'pages.contactLog',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <ContactLogPage />
              </Suspense>
            ),
            authority: [
              EnumsValues.Functions.ContactLogRead,
              EnumsValues.Functions.ContactLogReadChair,
            ],
            key: PageKey.ContactLog,
            hideInMenu: false,
          },
        ],
      },

      {
        path: '/app/prospection',
        component: EmptyLayout,
        authority: EnumsValues.Functions.ProspectRead,
        name: 'Prospección',
        translation_key: 'pages.prospection',
        icon: <SolutionOutlined />,
        key: PageKey.Prospection,
        requiresTenantSelected: true,
        hideInMenu: (routeConfig) => {
          return !routeConfig.isChairAccount && !routeConfig.isStaffAccount;
        },
        routes: [
          {
            path: '/app/prospection/prospects',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <ProspectsPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.ProspectRead,
            name: 'Gestión de Prospectos',
            translation_key: 'pages.communityProspect',
            key: PageKey.CommunityProspect,
            requiresTenantSelected: true,
          },
          {
            path: '/app/prospection/opportunities',
            name: 'Base de Contactos',
            translation_key: 'pages.communityOpportunities',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <ContactsPage mode="opportunities" />
              </Suspense>
            ),
            authority: EnumsValues.Functions.ReadOpportunity,
            key: PageKey.Opportunities,
          },
          {
            path: '/app/prospection/contactLog',
            name: 'Bitácora de Contacto',
            translation_key: 'pages.contactLog',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <ContactLogPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.ContactLogReadChair,
            key: PageKey.ContactLogChair,
            hideInMenu: (routeConfig) => !routeConfig.isChairAccount,
          },
          // {
          //   path: '/app/prospection/groupLog',
          //   name: 'Bitácora de Grupo',
          //   translation_key: 'pages.groupLog',
          //   component: () => (
          //     <Suspense fallback={<PageLoading />}>
          //       <GroupLogPage />
          //     </Suspense>
          //   ),
          //   authority: EnumsValues.Functions.GroupLogReadChair,
          //   key: PageKey.GroupLogChair,
          //   hideInMenu: true,
          // },
        ],
      },
      {
        path: '/app/entryForms',
        component: EmptyLayout,
        authority: EnumsValues.Functions.EntryFormRead,
        name: 'Fichas de ingreso',
        translation_key: 'pages.entryForm',
        icon: <FileDoneOutlined />,
        key: PageKey.EntryForm,
        requiresTenantSelected: true,
        hideInMenu: (routeConfig, _isAppMultitenant, isAdminOrStaff) => {
          return (
            !routeConfig.isChairAccount &&
            !routeConfig.isStaffAccount &&
            !isAdminOrStaff
          );
        },
        routes: [
          {
            path: '/app/entryForms/list',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <EntryFormsPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.EntryFormRead,
            name: 'Fichas de ingreso',
            translation_key: 'pages.entryFormList',
            key: PageKey.EntryFormList,
            requiresTenantSelected: true,
          },
          {
            path: '/app/entryForms/pending',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <PendingEntryFormsPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.EntryFormRead,
            name: 'Fichas pendientes',
            translation_key: 'pages.entryFormsPending',
            key: PageKey.EntryFormPending,
            requiresTenantSelected: true,
            hideInMenu: (routeConfig) => !routeConfig.isStaffAccount,
          },
        ],
      },
      {
         path: '/app/members',
        name: 'Miembros',
        translation_key: 'pages.membersChair',
        component: EmptyLayout,
        icon: <ContactsOutlined />,
        authority: EnumsValues.Functions.GroupRead,
        key: PageKey.membersChair,
        hideInMobileMenu: false,
        hideInMenu: (routeConfig) => {
          return !routeConfig.isChairAccount;
        },
        routes: [
           {
            path: '/app/members/myMembers',
            exact: true,
            name: 'Mis miembros',
            translation_key: 'pages.myMembers',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <MyMembersChairPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.GroupRead,
            key: PageKey.myMembers,
          },
          {
            path: '/app/members/requests',
            exact: true,
            name: 'Solicitudes',
            translation_key: 'pages.myRequests',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <ChairRequestsPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.RequestRead,
            key: PageKey.myRequests,
          },
        ]
      },
      {
        path: '/app/membersManagement',
        name: 'Gestión Miembros',
        translation_key: 'pages.membersManagement',
        component: EmptyLayout,
        icon: <ContactsOutlined />,
        authority: EnumsValues.Functions.IsStaffRole,
        key: PageKey.MembersManagement,
        hideInMobileMenu: false,
        hideInMenu: (routeConfig) => {
          return !routeConfig.isStaffAccount;
        },
        routes: [
          {
            path: '/app/membersManagement/programs',
            exact: true,
            name: 'Programas',
            translation_key: 'pages.programs',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <ProgramsPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.ProgramRead,
            key: PageKey.ProgramsList,
          },
          {
            path: '/app/membersManagement/programs/update-prices',
            name: 'Actualizar Precios',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <UpdatePricesPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.ProgramUpdate,
            key: PageKey.UpdatePrices,
            hideInMenu: true,
          },
          {
            path: '/app/membersManagement/groups',
            name: 'Grupos',
            translation_key: 'pages.groups',
            component: EmptyLayout,
            authority: EnumsValues.Functions.GroupRead,
            key: PageKey.GroupsManagement,
            hideInMenu: (routeConfig) => {
              return !routeConfig.isStaffAccount;
            },
            routes: [
              {
                path: '/app/membersManagement/groups/groups',
                name: 'Grupos',
                translation_key: 'pages.groups',
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <GroupsPage />
                  </Suspense>
                ),
                authority: EnumsValues.Functions.GroupRead,
                key: PageKey.GroupsPage,
              },
              {
                path: '/app/membersManagement/groups/groupLog',
                name: 'Bitácora de Grupo',
                translation_key: 'pages.groupLog',
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <GroupLogPage />
                  </Suspense>
                ),
                authority: [
                  EnumsValues.Functions.GroupLogRead,
                  EnumsValues.Functions.GroupLogReadChair,
                ],
                key: PageKey.GroupLog,
                hideInMenu: false,
              },
            ],
          },

          {
            path: '/app/membersManagement/community',
            name: 'Comunidad',
            translation_key: 'pages.community',
            component: EmptyLayout,
            authority: EnumsValues.Functions.ContactList,
            key: PageKey.Community,
            routes: [
              {
                path: '/app/membersManagement/community/staff',
                name: 'Staff',
                translation_key: 'pages.communityStaff',
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <StaffPage />
                  </Suspense>
                ),
                authority: EnumsValues.Functions.ContactList,
                key: PageKey.CommunityStaff,
              },
              {
                path: '/app/membersManagement/community/chair',
                name: 'Chair',
                translation_key: 'pages.communityChair',
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <ChairPage />
                  </Suspense>
                ),
                authority: EnumsValues.Functions.ContactList,
                key: PageKey.CommunityChair,
                hideInMenu: (routeConfig) => {
                  return !routeConfig.isStaffAccount;
                },
              },
              {
                path: '/app/membersManagement/community/members',
                name: 'Miembros',
                translation_key: 'pages.communityMembers',
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <MembersPage />
                  </Suspense>
                ),
                authority: EnumsValues.Functions.ContactList,
                key: PageKey.CommunityMembers,
                hideInMenu: (routeConfig) => {
                  return !routeConfig.isStaffAccount;
                },
              },
            ],
          },
            {
                path: '/app/membersManagement/request',
                name: 'Solicitudes',
                translation_key: 'pages.requests',
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <RequestsPage />
                  </Suspense>
                ),
                authority: EnumsValues.Functions.RequestRead,
                key: PageKey.Requests,
                hideInMenu: (routeConfig) => {
                  return !routeConfig.isStaffAccount;
                },
          },
          {
                path: '/app/membersManagement/leavePolls',
                name: 'Encuestas de baja',
                translation_key: 'pages.leavePolls',
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <LeavePollsPage />
                  </Suspense>
                ),
                authority: EnumsValues.Functions.LeaveFormRead,
                key: PageKey.LeavePolls,
                hideInMenu: (routeConfig) => {
                  return !routeConfig.isStaffAccount;
                },
          }
        ],
      },
      {
        path: '/app/groupsManagement',
        name: 'Gestión Grupos',
        translation_key: 'pages.groupsManagement',
        component: EmptyLayout,
        icon: <ContactsOutlined />,
        authority: EnumsValues.Functions.GroupRead,
        key: PageKey.GroupsManagement,
        hideInMobileMenu: false,
        hideInMenu: true,
        routes: [
          {
            path: '/app/groupsManagement/groups',
            name: 'Grupos',
            translation_key: 'pages.groups',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <GroupsPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.GroupRead,
            key: PageKey.GroupsPage,
          },
          {
            path: '/app/groupsManagement/groupLog',
            name: 'Bitácora de Grupo',
            translation_key: 'pages.groupLog',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <GroupLogPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.GroupLogRead,
            key: PageKey.GroupLog,
            hideInMenu: false,
          },
        ],
      },
      {
        path: '/app/reports/:reportCode',
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <ReportDetailPage />
          </Suspense>
        ),
        key: PageKey.ReportDetail,
        requiresTenantSelected: true,
        hideInMenu: true,
      },
      {
        path: '/app/help-desk',
        name: 'Atención a Miembros',
        translation_key: 'pages.helpDesk',
        component: EmptyLayout,
        icon: <PhoneOutlined />,
        authority: EnumsValues.Functions.IsStaffRole,
        key: PageKey.HelpDesk,
        hideInMobileMenu: false,
        hideInMenu: (routeConfig) => {
          return !routeConfig.isStaffAccount;
        },
        routes: [
          {
            path: '/app/help-desk/zoho-desk',
            component: () => <ZohoDesk />,
            // authority: EnumsValues.Functions.UserRoleList,
            name: 'Zoho Desk',
            translation_key: 'pages.zohoDesk',
            key: PageKey.ZohoDesk,
            requiresTenantSelected: false, //cambiar
          },
          {
            path: '/app/help-desk/board',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                {' '}
                <Board />
              </Suspense>
            ),
            name: 'Tablero',
            translation_key: 'pages.board',
            key: PageKey.Board,
            requiresTenantSelected: false, //cambiar
          },
          {
            path: '/app/help-desk/complete-detail',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                {' '}
                <CompleteDetail />
              </Suspense>
            ),
            name: 'Detalle Completo',
            translation_key: 'pages.completeDetail',
            key: PageKey.CompleteDetail,
            requiresTenantSelected: false, //cambiar
          },
        ],
      },
            {
        path: '/app/my-exhibitor-profile',
        authority: EnumsValues.Functions.ExhibitorSelfRead,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <MyExhibitorProfilePage />
          </Suspense>
        ),
        name: 'Mi ficha',
        translation_key: 'pages.myExhibitorProfile',
        icon: <ProfileOutlined />,
        key: PageKey.MyExhibitorProfile,
        requiresTenantSelected: true,
        hideInMenu: (routeConfig) => !routeConfig.isExhibitorAccount,
      },
      {
        path: '/app/reunions',
        name: 'Reuniones',
        translation_key: 'pages.reunions',
        component: EmptyLayout,
        icon: <TeamOutlined />,
        authority: [EnumsValues.Functions.ExhibitorRead, EnumsValues.Functions.IsStaffRole],
        key: PageKey.ExhibitorReunions,
        hideInMobileMenu: false,
        hideInMenu: (routeConfig) => {
          return !routeConfig.isStaffAccount && !routeConfig.isChairAccount && !routeConfig.isExhibitorAccount;
        },
        routes: [
          {
            path: '/app/reunions/meetings',
            name: 'Reuniones',
            translation_key: 'pages.meetings',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <MeetingsPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.ExhibitorRead,
            key: PageKey.Meetings,
            hideInMobileMenu: false,
            hideInMenu: (routeConfig) => !!routeConfig.isExhibitorAccount,
            requiresTenantSelected: true,
          },
          {
            path: '/app/reunions/exhibitors',
            name: 'Expositores',
            translation_key: 'pages.exhibitors',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <ExhibitorPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.ExhibitorRead,
            key: PageKey.Exhibitor,
            hideInMobileMenu: false,
            hideInMenu: (routeConfig) => !!routeConfig.isExhibitorAccount,
          },
          {
            path: '/app/reunions/topics',
            name: 'Temas',
            translation_key: 'pages.topics',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <TopicsPage />
              </Suspense>
            ),
            authority: [EnumsValues.Functions.ExhibitorRead, EnumsValues.Functions.IsStaffRole, EnumsValues.Functions.TopicRead],
            key: PageKey.Topics,
            hideInMobileMenu: false,
            requiresTenantSelected: true,
          },
          {
            path: '/app/reunions/presentations',
            name: 'Presentaciones',
            translation_key: 'pages.presentations',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <PresentationsPage />
              </Suspense>
            ),
            authority: [
              EnumsValues.Functions.PresentationRead,
              EnumsValues.Functions.IsStaffRole,
              EnumsValues.Functions.ExhibitorRead,
            ],
            key: PageKey.Presentations,
            hideInMobileMenu: false,
            requiresTenantSelected: true,
          },
        ],
      },
      {
        path: '/app/comunication',
        name: 'Comunicación',
        translation_key: 'pages.comunication',
        component: EmptyLayout,
        icon: <SoundOutlined />,
        authority: EnumsValues.Functions.EventRead,
        key: PageKey.Comunication,
        hideInMobileMenu: false,
        hideInMenu: (routeConfig) => {
          return !routeConfig.isStaffAccount;
        },
        routes: [
          // Oculto temporalmente: la funcionalidad de Eventos aún no está disponible y confunde a los usuarios.
          // {
          //   path: '/app/comunication/events',
          //   name: 'Eventos',
          //   translation_key: 'pages.events',
          //   component: () => (
          //     <Suspense fallback={<PageLoading />}>
          //       <EventsPage />
          //     </Suspense>
          //   ),
          //   authority: EnumsValues.Functions.EventRead,
          //   key: PageKey.Events,
          //   hideInMobileMenu: false,
          // },
                    {
            path: '/app/comunication/notifications',
            authority: EnumsValues.Functions.NotificationRead,
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <NotificationsPage />
              </Suspense>
            ),
            name: 'Notificacions',
            translation_key: 'pages.notifications',
            key: PageKey.Notifications,
          },
        ],
      },
      {
        path: '/app/surveys',
        name: 'Encuestas',
        translation_key: 'pages.surveys',
        component: EmptyLayout,
        icon: <FileDoneOutlined />,
        authority: EnumsValues.Functions.SurveyCampaignRead,
        key: PageKey.Surveys,
        hideInMobileMenu: false,
        hideInMenu: (routeConfig) => {
          return !routeConfig.isStaffAccount;
        },
        routes: [
          {
            path: '/app/surveys/campaigns',
            name: 'Campañas',
            translation_key: 'pages.surveyCampaigns',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <SurveyCampaignsPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.SurveyCampaignRead,
            key: PageKey.SurveyCampaigns,
            hideInMobileMenu: false,
          },
        ],
      },
      {
        path: '/app/administration',
        name: 'Administración',
        translation_key: 'pages.administration',
        component: EmptyLayout,
        icon: <ControlOutlined />,
        authority: EnumsValues.Functions.AdministrationMenuRead,
        key: PageKey.Administration,
        hideInMobileMenu: false,
        hideInMenu: (routeConfig) => {
          return !routeConfig.isStaffAccount;
        },
        routes: [
          {
            path: '/app/administration/programs',
            component: EmptyLayout,
            name: 'Programas',
            translation_key: 'pages.configPrograms',
            authority: EnumsValues.Functions.ProgramRead,
            key: PageKey.ConfigPrograms,
            routes: [
              {
                path: '/app/administration/programs/categories',
                name: 'Categorías',
                translation_key: 'pages.programCategories',
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <ProgramCategoriesPage />
                  </Suspense>
                ),
                authority: EnumsValues.Functions.CategoryRead,
                key: PageKey.ProgramCategories,
              },
              {
                path: '/app/administration/programs/variants',
                name: 'Variantes',
                translation_key: 'pages.programVariants',
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <ProgramVariantsPage />
                  </Suspense>
                ),
                authority: EnumsValues.Functions.ProgramRead,
                key: PageKey.ProgramVariants,
              },
              {
                path: '/app/administration/programs/regions',
                name: 'Regiones',
                translation_key: 'pages.programRegions',
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <ProgramRegionsPage />
                  </Suspense>
                ),
                authority: EnumsValues.Functions.ProgramRead,
                key: PageKey.ProgramRegions,
              },
              {
                path: '/app/administration/programs/usa-programs',
                name: 'Programas USA',
                translation_key: 'pages.usaPrograms',
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <UsaProgramsPage />
                  </Suspense>
                ),
                authority: EnumsValues.Functions.ProgramRead,
                key: PageKey.UsaPrograms,
              },
            ],
          },
          {
            path: '/app/administration/groups',
            // authority: EnumsValues.Functions.NotificationRead,
            component: EmptyLayout,
            name: 'Grupos',
            translation_key: 'pages.groups',
            key: PageKey.Groups,
            hideInMenu: (routeConfig) => {
              return !routeConfig.isStaffAccount;
            },
            routes: [
              {
                path: '/app/administration/groups/VCG',
                // authority: EnumsValues.Functions.VCG_Read,
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <VcgPage />
                  </Suspense>
                ),
                name: 'VCG',
                translation_key: 'pages.vcg',
                key: PageKey.VCG,
              },
            ],
          },
          {
            path: '/app/administration/exhibitors',
            authority: EnumsValues.Functions.ExhibitionCategoryRead,
            component: EmptyLayout,
            name: 'Expositores',
            translation_key: 'pages.exhibitors',
            key: PageKey.ExhibitorsSettings,
            routes: [
              {
                path: '/app/administration/exhibitors/categories',
                authority: EnumsValues.Functions.ExhibitionCategoryRead,
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <Categories />
                  </Suspense>
                ),
                name: 'Categorías',
                translation_key: 'pages.categories',
                key: PageKey.ExhibitorCategories,
              },
              {
                path: '/app/administration/exhibitors/subcategories',
                authority: EnumsValues.Functions.ExhibitionCategoryRead,
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <Subcategories />
                  </Suspense>
                ),
                name: 'Subcategorías',
                translation_key: 'pages.subcategories',
                key: PageKey.ExhibitorSubcategories,
              },
            ],
          },
          {
            path: '/app/administration/company',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <CompanyPage />
              </Suspense>
            ),
            name: 'Empresas',
            translation_key: 'pages.companies',
            authority: EnumsValues.Functions.CompanyList,
            key: PageKey.Company,
          },
          {
            path: '/app/administration/taxCondition',
            authority: EnumsValues.Functions.TaxConditionRead,
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <TaxConditionPage />
              </Suspense>
            ),
            name: 'Condición Impositiva',
            translation_key: 'pages.taxCondition',
            key: PageKey.TaxCondition,
          },
          {
            path: '/app/administration/hitos',
            component: EmptyLayout,
            name: 'Hitos',
            translation_key: 'pages.hitos',
            authority: EnumsValues.Functions.HitoTipoRead,
            key: PageKey.Hitos,
            routes: [
              {
                path: '/app/administration/hitos/types',
                authority: EnumsValues.Functions.HitoTipoRead,
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <HitoTiposPage />
                  </Suspense>
                ),
                name: 'Tipos de Hitos',
                translation_key: 'pages.hitoTipos',
                key: PageKey.HitoTipos,
              },
              {
                path: '/app/administration/hitos/subtypes',
                authority: EnumsValues.Functions.HitoTipoRead,
                component: () => (
                  <Suspense fallback={<PageLoading />}>
                    <HitoSubtiposPage />
                  </Suspense>
                ),
                name: 'Subtipos de Hitos',
                translation_key: 'pages.hitoSubtipos',
                key: PageKey.HitoSubtipos,
              },
            ],
          },
          {
            path: '/app/administration/termsAndPrivacyPolicy',
            component: (props: any) => (
              <Suspense fallback={<PageLoading />}>
                <ManageTermsAndConditionsPage {...props} />
              </Suspense>
            ),
            authority: EnumsValues.Functions.TermsAndConditionsUpdate,
            name: 'Términos y condiciones',
            translation_key: 'message.termsAndConditions',
            headerName: 'pages.termsAndConditions',
            key: PageKey.ManageTermsAndConditions,
            requiresTenantSelected: true,
          },
          {
            path: '/app/administration/setting',
            authority: EnumsValues.Functions.AppSettingRead,
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <AppSettingPage />
              </Suspense>
            ),
            name: 'Generales',
            translation_key: 'pages.configuration',
            key: PageKey.Setting,
          },
        ],
      },

      {
        path: '/app/guides',
        name: 'Guías',
        translation_key: 'pages.guides',
        component: EmptyLayout,
        icon: <QuestionCircleOutlined />,
        // authority: EnumsValues.Functions.IsStaffRole,
        key: PageKey.Guides,
        hideInMobileMenu: false,
        hideInMenu: (routeConfig) => {
          return !!routeConfig.isExhibitorAccount;
        },
        routes: [
          {
            path: '/app/guides/chairs',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                {' '}
                <GuideChairs />
              </Suspense>
            ),
            authority: EnumsValues.Functions.ChairGuideRead,
            name: 'Guía Chairs',
            translation_key: 'pages.guideChairs',
            key: PageKey.GuideChairs,
            requiresTenantSelected: false, //cambiar
          },
          {
            path: '/app/guides/staff',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                {' '}
                <GuideStaff />
              </Suspense>
            ),
            authority: EnumsValues.Functions.IsStaffRole,
            name: 'Guía Staff',
            translation_key: 'pages.guideStaff',
            key: PageKey.GuideStaff,
            requiresTenantSelected: false, //cambiar
            hideInMenu: (routeConfig) => {
              return !routeConfig.isStaffAccount;
            },
          },
        ],
      },
      {
        path: '/app/myAccount',
        authority: EnumsValues.Functions.TenantRead,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <MyAccountPage />
          </Suspense>
        ),
        name: 'Mi cuenta',
        translation_key: 'pages.myAccount',
        icon: <LocationHomeIcon />,
        key: PageKey.MyAccount,
        hideInMenu: (routeConfig) => {
          return !!routeConfig.isStaffAccount || !!routeConfig.isExhibitorAccount;
        },
      },

      {
        path: '/app/config2fa',
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <Config2FA />
          </Suspense>
        ),
        hideInMenu: true,
        name: 'Autenticación 2FA',
        translation_key: 'pages.auth2FA',
        icon: <LockOutlined />,
        key: PageKey.Config2fa,
        requiresTenantSelected: true,
      },
      {
        path: '/app/settingAdministration',
        name: 'Configuración',
        translation_key: 'pages.settingAdministration',
        key: PageKey.SettingAdminstration,
        component: EmptyLayout,
        icon: <SettingOutlined />,
        authority: EnumsValues.Functions.SettingAdministrationMenuRead,
        hideInMobileMenu: false,
        hideInMenu: (routeConfig) => {
          return !routeConfig.isStaffAccount;
        },
        routes: [
          {
            path: '/app/settingAdministration/user',
            component: (props: any) => (
              <Suspense fallback={<PageLoading />}>
                <UserPage {...props} />
              </Suspense>
            ),
            authority: EnumsValues.Functions.UserRoleList,
            name: 'Usuarios',
            translation_key: 'pages.users',
            key: PageKey.User,
            requiresTenantSelected: true,
          },
          {
            path: '/app/settingAdministration/role',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <RolePage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.UserRoleList,
            name: 'Roles',
            translation_key: 'pages.roles',
            key: PageKey.Role,
            requiresTenantSelected: true,
          },
          {
            path: '/app/settingAdministration/permission',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <PermissionPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.PermissionList,
            name: 'Permisos',
            translation_key: 'pages.permissions',
            key: PageKey.Permission,
            requiresTenantSelected: true,
          },
          {
            path: '/app/settingAdministration/tenant',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <TenantPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.TenantRead,
            name: 'Tenants',
            translation_key: 'pages.vistageAccounts',
            key: PageKey.Tenant,
            hideInMenu: false,
            requiresTenantSelected: true,
          },
          {
            path: '/app/settingAdministration/exhibitors',
            authority: EnumsValues.Functions.SupplierRead,
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <SuppliersPage />
              </Suspense>
            ),
            name: 'Expositores',
            translation_key: 'pages.exhibitors',
            key: PageKey.ExhibitorsAdministration,
            hideInMenu: true,
          },
          {
            path: '/app/settingAdministration/newExhibitor',
            // authority: EnumsValues.Functions.SupplierCreate,
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <SupplierManagementPage />
              </Suspense>
            ),
            name: 'Nuevo expositor',
            hideInMenu: true,
            translation_key: 'pages.newExhibitor',
            key: PageKey.NewExhibitor,
          },
          {
            path: '/app/settingAdministration/updateExhibitor/:id',
            authority: EnumsValues.Functions.SupplierUpdate,
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <SupplierManagementPage editMode />
              </Suspense>
            ),
            name: 'Actualizar expositor',
            hideInMenu: true,
            translation_key: 'pages.updateExhibitor',
            key: PageKey.UpdateExhibitor,
          },

          {
            path: '/app/settingAdministration/audit',
            component: () => (
              <Suspense fallback={<PageLoading />}>
                <AuditPage />
              </Suspense>
            ),
            authority: EnumsValues.Functions.AuditEventRead,
            name: 'Auditoría',
            translation_key: 'pages.audit',
            key: PageKey.Audit,
            requiresTenantSelected: true,
          },
        ],
      },
      {
        path: '/app/accountAdministration',
        name: 'Cuenta',
        translation_key: 'pages.accountAdministration',
        component: EmptyLayout,
        icon: <ControlOutlined />,
        authority: EnumsValues.Functions.AccountAdministrationMenuRead,
        key: PageKey.AccountAdministration,
        hideInMobileMenu: false,
        hideInMenu: (routeConfig) => {
          return !!routeConfig.isExhibitorAccount;
        },
        routes: [
          {
            path: '/app/accountAdministration/user',
            component: (props: any) => (
              <Suspense fallback={<PageLoading />}>
                <UserPage {...props} />
              </Suspense>
            ),
            authority: EnumsValues.Functions.UserRoleList,
            name: 'Usuarios',
            translation_key: 'pages.users',
            key: PageKey.User,
            requiresTenantSelected: true,
          },
        ],
      },
      {
        path: '/app/invitations',
        component: (props: any) => (
          <Suspense fallback={<PageLoading />}>
            <AdminInvitationPage {...props} />
          </Suspense>
        ),
        authority: EnumsValues.Functions.UserCreate,
        hideInMenu: true,
        name: 'Invitaciones',
        translation_key: 'pages.invitations',
        key: PageKey.Invitations,
        requiresTenantSelected: true,
      },
      {
        path: '/app/termsAndPrivacy',
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <TermsAndConditionsPage mode="registered" />
          </Suspense>
        ),
        name: 'Términos y condiciones',
        translation_key: 'message.termsAndConditions',
        headerName: 'pages.termsAndConditions',
        icon: <FileTextOutlined />,
        key: PageKey.TermsAndConditions,
      },
      {
        path: '/app/about',
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <AboutPage />
          </Suspense>
        ),
        name: 'Acerca de',
        translation_key: 'pages.about',
        icon: <BulbOutlined />,
        key: PageKey.About,
      },
      {
        path: '/app/selectTenant',
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <SelectTenantPage />
          </Suspense>
        ),
        hideInMenu: true,
        key: PageKey.SelectTenant,
      },
      {
        path: '/app/profile',
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <ProfilePage />
          </Suspense>
        ),
        name: 'Mi perfil',
        translation_key: 'pages.myProfile',
        hideInMenu: true,
        key: PageKey.Profile,
      },
      {
        path: '/app/status',
        authority: EnumsValues.Functions.IsAdmin,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <StatusPage />
          </Suspense>
        ),
        name: 'Status info',
        translation_key: 'pages.statusInfo',
        icon: <InfoCircleOutlined />,
        key: PageKey.Status,
      },
      redirectIfNotExist,
    ],
  },
  {
    path: '/',
    component: (props: any) => (
      <Suspense fallback={<PageLoading />}>
        <PublicLayout {...props} />
      </Suspense>
    ),
    key: PageKey.PublicLayout,
    routes: [
      {
        path: '/resetPassword',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <ResetPasswordContentPage />
          </Suspense>
        ),
        key: PageKey.ResetPassword,
      },
      {
        exact: true,
        path: '/setPassword',
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <SetPasswordContentPage />
          </Suspense>
        ),
        key: PageKey.SetPassword,
      },
      {
        exact: true,
        path: '/completeRegister',
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <CompleteRegisterPage />
          </Suspense>
        ),
        key: PageKey.CompleteRegister,
      },
      {
        exact: true,
        path: '/termsAndPrivacyPolicy',
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <TermsAndConditionsPage mode="public" />
          </Suspense>
        ),
        key: PageKey.TermsAndConditions,
      },
      {
        path: '/login',
        exact: true,
        component: (props: any) => (
          <Suspense fallback={<PageLoading />}>
            <LoginPage {...props} />
          </Suspense>
        ),
        key: PageKey.Login,
      },
      {
        path: '/mustChangePassword',
        exact: true,
        component: (props: any) => (
          <Suspense fallback={<PageLoading />}>
            <MustChangePasswordPage {...props} />
          </Suspense>
        ),
        key: PageKey.MustChangePassword,
      },
      {
        path: '/register',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <RegisterPage />
          </Suspense>
        ),
        key: PageKey.Register,
      },
      {
        path: '/reminder',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <Reminder2FA />
          </Suspense>
        ),
        key: PageKey.Reminder,
      },
      {
        path: '/generate',
        exact: true,
        component: (props: any) => (
          <Suspense fallback={<PageLoading />}>
            <Generate2FA {...props} />
          </Suspense>
        ),
        key: PageKey.Generate,
      },
      {
        path: '/validateTotp',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <Validate2FA />
          </Suspense>
        ),
        key: PageKey.ValidateTotp,
      },
      {
        path: '/invitation',
        exact: true,
        component: (props: any) => (
          <Suspense fallback={<PageLoading />}>
            <InvitationPage {...props} />
          </Suspense>
        ),
        translation_key: 'pages.invitation',
        key: PageKey.Invitation,
      },
      {
        path: '/evaluation/:hash',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <EvaluationForm />
          </Suspense>
        ),
        key: PageKey.EvaluationForm,
      },
      {
        path: '/evaluation/meeting/:hash',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <GroupEvaluationPage />
          </Suspense>
        ),
        key: PageKey.EvaluationForm,
      },
      {
        path: '/complete-form',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <ExhibitorFormPage />
          </Suspense>
        ),
        translation_key: 'pages.exhibitorFormPage',
        hideInMenu: true,
        key: PageKey.ExhibitorFormPage,
      },
      {
        path: '/presenter-invoice',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <ExternalPresenterInvoiceFormPage />
          </Suspense>
        ),
        translation_key: 'pages.externalPresenterInvoiceFormPage',
        hideInMenu: true,
        key: PageKey.ExternalPresenterInvoiceFormPage,
      },
      {
        path: '/ingreso',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <EntryFormPublicPage />
          </Suspense>
        ),
        translation_key: 'pages.entryFormPublicPage',
        hideInMenu: true,
        key: PageKey.EntryFormPublicPage,
      },
      {
        path: '/maintenance',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <MaintenancePage />
          </Suspense>
        ),
        name: 'En mantenimiento',
        translation_key: 'pages.inMaintenance',
        hideInMenu: true,
        key: PageKey.Maintenance,
      },
      {
        path: '/resendConfirmationAccountEmail',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <ResendConfirmationAccountEmailPage />
          </Suspense>
        ),
        key: PageKey.ResendConfirmationAccountEmailPage,
      },
      {
        path: '/orderPayment',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <OrderPaymentPage />
          </Suspense>
        ),
        key: PageKey.OrderPaymentPage,
      },
      {
        path: '/ev/:permaLink',
        exact: true,
        component: () => (
          <Suspense fallback={<PageLoading />}>
            <LandingEventPage />
          </Suspense>
        ),
        key: PageKey.LandingEvent,
      },
      redirectIfNotExist,
    ],
  },
];

export default routes;
